<script setup lang="ts">
import { computed } from 'vue';

interface SectionVM {
  id: number | string;
  sort?: number | null;
  side?: 'left' | 'right' | null;
  svg_inline?: string | null;
  sticky?: boolean | null;
  Color_1?: string | null;
  Color_2?: string | null;
  Color_3?: string | null;
  Color_4?: string | null;
}

const width = '100%'; // responsive width for gutters
// const width = '80px'; // fixed width for gutters
const width_css = 'w-[' + width + ']';

const props = defineProps<{ section: SectionVM }>();

const isLeft = computed(() => props.section.side === 'left');
const stripClasses = computed(() => (props.section.sticky ? 'sticky top-0 h-screen ' + width_css : 'h-full'));

const colors = computed(() => {
  const arr = [props.section.Color_1, props.section.Color_2, props.section.Color_3, props.section.Color_4].map((c) =>
    c && String(c).trim() ? String(c) : null,
  );
  return arr as [string, string, string, string];
});

/**
 * Strategy A: if the SVG uses CSS variables var(--c1..--c4), we just set them.
 */
const cssVarStyle = computed(() => ({
  '--c1': colors.value[0],
  '--c2': colors.value[1],
  '--c3': colors.value[2],
  '--c4': colors.value[3],
}));

function recolorSectionWaves(svg: string, colors: Array<string | null>) {
  // ---- Pass 1: recolor visible <path> (not in <defs>) ----
  let i = 0; // index in "visible paths" order
  let out = '';
  let last = 0;
  let defsDepth = 0;

  // simple tag scanner
  const TAG_RE = /<[^>]+>/g;
  let m: RegExpExecArray | null;

  while ((m = TAG_RE.exec(svg))) {
    const tag = m[0];
    const before = svg.slice(last, m.index);
    last = TAG_RE.lastIndex;

    // Detect <defs ...>, </defs>
    const isOpenDefs = /^<\s*defs\b/i.test(tag) && !/\/>\s*$/.test(tag);
    const isCloseDefs = /^<\s*\/\s*defs\s*>/i.test(tag);

    if (isOpenDefs) {
      defsDepth++;
      out += before + tag;
      continue;
    }
    if (isCloseDefs) {
      defsDepth = Math.max(0, defsDepth - 1);
      out += before + tag;
      continue;
    }

    // Process <path ...> tags only when not in <defs>
    const isPath = /^<\s*path\b/i.test(tag);
    if (!isPath || defsDepth > 0) {
      out += before + tag;
      continue;
    }

    // We're on a visible <path>. Apply recolor logic for index i.
    const color = i < colors.length ? colors[i] : null;
    i++; // increment for each visible path, regardless of recolor

    let newTag = tag;

    if (color !== null && color !== '') {
      // 1) Remove any inline style fill: ... from style=""
      newTag = newTag.replace(/\sstyle=(["'])(.*?)\1/is, (_m, q, val: string) => {
        const parts = val
          .split(';')
          .map((s) => s.trim())
          .filter(Boolean)
          .filter((kv) => !/^fill\s*:/i.test(kv));
        return parts.length ? ` style=${q}${parts.join(';')}${q}` : '';
      });

      // 2) Remove any fill="..."
      newTag = newTag.replace(/\sfill=(["']).*?\1/is, '');

      // 3) Add our fill before closing
      newTag = newTag.replace(/\/?>\s*$/s, (end) => ` fill="${color}"${end}`);
    }
    // else: leave path as-is

    out += before + newTag;
  }
  out += svg.slice(last);

  // ---- Pass 2: prefix IDs and update clip-path url(#...) references ----
  const uid = `sec${Math.random().toString(36).slice(2)}`;
  const oldIds: string[] = [];

  // Collect ids first (before changing them)
  out.replace(/\sid=(["'])([^"']+)\1/g, (_m, _q, id: string) => {
    oldIds.push(id);
    return _m;
  });

  // Prefix ids
  out = out.replace(/\sid=(["'])([^"']+)\1/g, (_m, q, id: string) => {
    return ` id=${q}${uid}-${id}${q}`;
  });

  // Update clip-path="url(#oldId)" to new prefixed id
  for (const id of oldIds) {
    const reDq = new RegExp(`(\\sclip-path=)(")url\\(#${escapeReg(id)}\\)("\\s|>)`, 'g');
    const reSq = new RegExp(`(\\sclip-path=)(')url\\(#${escapeReg(id)}\\)('\\s|>)`, 'g');
    out = out.replace(reDq, (_m, pre, q1, q2) => `${pre}${q1}url(#${uid}-${id})${q2}`);
    out = out.replace(reSq, (_m, pre, q1, q2) => `${pre}${q1}url(#${uid}-${id})${q2}`);
  }

  return out;
}

// util: escape string for use in RegExp
function escapeReg(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Final SVG string we will inject.
 * Order of precedence:
 *  1) If svg uses CSS vars -> keep as-is (vars applied via wrapper style)
 *  2) Else -> rewrite literal fills for first 4 bands
 */
const svgHtml = computed(() => {
  const raw = computeSvg();
  if (!raw) return '';
  // If the SVG uses CSS variables, we don't need to mutate it
  if (/var\(--c\d\)/.test(raw)) return raw;
  // Otherwise, we rewrite the fills for the first 4 bands
  // return recolorSvgLiteral(raw, colors.value);
  return recolorSectionWaves(raw, colors.value);
});

function computeSvg(): string {
  let raw = props.section.svg_inline || '';
  if (!raw) return '';

  // make width full width
  const widthMatch = raw.match(/<svg[^>]+width\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/i);
  if (widthMatch) {
    const widthAttr = widthMatch[1];
    const newWidth = widthAttr.startsWith('"') || widthAttr.startsWith("'") ? `"${width}"` : `${width}`;
    raw = raw.replace(widthAttr, newWidth);
  } else {
    // no width attr, add it
    raw = raw.replace(/<svg([^>]*)>/i, `<svg width="100%"$1>`);
  }
  // make height auto
  const heightMatch = raw.match(/<svg[^>]+height\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/i);
  if (heightMatch) {
    const heightAttr = heightMatch[1];
    const newHeight = heightAttr.startsWith('"') || heightAttr.startsWith("'") ? `"100%"` : `100%`;
    raw = raw.replace(heightAttr, newHeight);
  } else {
    // no height attr, add it
    // raw = raw.replace(/<svg([^>]*)>/i, `<svg height="auto"$1>`);
  }

  // remove width to only fit height
  // raw = raw.replace(/<svg[^>]+width\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)\s*/i, '<svg ');
  // add class for absolute pckend + CMSositioning
  if (isLeft.value) {
    raw = raw.replace(/<svg([^>]*)>/i, `<svg class="absolute top-0 left-0 bg-transparent"$1>`);
  } else {
    raw = raw.replace(/<svg([^>]*)>/i, `<svg class="absolute top-0 right-0 bg-transparent"$1>`);
  }

  // allow resizing to be non proportional with preserveAspectRatio="none"
  raw = raw.replace(/<svg([^>]*)>/i, `<svg preserveAspectRatio="none"$1>`);

  return raw;
}

const pageRefresh = inject<() => Promise<void>>('pageRefresh');
// Helper functions for Visual Editing
function applyVisualEditing() {
  apply({
    onSaved: async () => {
      await pageRefresh?.();
    },
  });
}
const { isVisualEditingEnabled, apply, setAttr } = useVisualEditing();
function applyVisualEditingButton() {
  apply({
    elements: document.querySelector('.visual-editing-button-blocks') as HTMLElement,
    customClass: 'visual-editing-button-class',
    onSaved: async () => {
      await pageRefresh?.();
      // This makes sure the visual editor elements are updated after the page is refreshed. In case you've added new blocks to the page.
      await nextTick();
      applyVisualEditing();
    },
  });
}

onMounted(() => {
  if (!isVisualEditingEnabled.value) return;
  applyVisualEditingButton();
  applyVisualEditing();
});
</script>

<template>
  <section
    class="relative"
    :data-directus="
      setAttr({
        collection: 'pages_sections',
        item: section.id,
        fields: [
          'name',
          'Color_1_override',
          'Color_2_override',
          'Color_3_override',
          'Color_4_override',
          'sticky_override',
          'side_override',
          'blocks',
          'm2a_button',
        ],
        mode: 'modal',
      })
    "
  >
    <div class="relative" :class="isLeft ? 'grid-cols-[150px_minmax(0,1fr)]' : 'grid-cols-[minmax(0,1fr)_150px]'">
      <!-- :data-directus=" -->
      <!-- setAttr({ -->
      <!-- collection: 'pages_sections', -->
      <!-- item: section.id, -->
      <!-- fields: ['name', 'Color_1_override', 'blocks', 'm2a_button'], -->
      <!-- mode: 'modal', -->
      <!-- }) -->
      <!-- " -->
      <!-- Left gutter -->
      <div
        v-if="isLeft && section.svg_inline"
        class="w-[30px] sm:w-[40px] md:w-[50px] h-full bg-transparent absolute top-0 -left-[--tapis] z-10 pointer-events-none"
      >
        <div :class="stripClasses" :style="cssVarStyle" aria-hidden="true" class="bg-transparent">
          <div
            v-if="svgHtml"
            class="h-full w-full relative bg-transparent"
            v-html="svgHtml"
            :data-directus="
              setAttr({
                collection: 'pages_sections',
                item: section.id,
                fields: [
                  'Color_1_override',
                  'Color_2_override',
                  'Color_3_override',
                  'Color_4_override',
                  'sticky_override',
                  'side_override',
                ],
                mode: 'modal',
              })
            "
          />
        </div>
      </div>
      <!-- Main content -->
      <div
        :class="
          section.svg_inline ? (isLeft ? 'pl-10px sm:pl-[20px] md:pl-[30px]' : 'pr-10px sm:pr-[20px] md:pr-[30px]') : ''
        "
        class="z-20"
      >
        <slot :section="section" v-if="section.blocks.length"></slot>
        <div class="p-4" v-else>
          <div class="flex h-[700px] place-items-center">
            <p class="text-gray-500 text-center w-full" v-if="isVisualEditingEnabled">Section vide</p>
          </div>
        </div>
      </div>

      <!-- Right gutter -->
      <div
        v-if="!isLeft && section.svg_inline"
        class="w-[30px] sm:w-[50px] md:w-[80px] h-full bg-transparent absolute top-0 -right-[--tapis] z-10 pointer-events-none"
      >
        <div :class="stripClasses" :style="cssVarStyle" aria-hidden="true" class="bg-transparent">
          <div
            v-if="svgHtml"
            class="h-full w-full relative bg-transparent"
            v-html="svgHtml"
            :data-directus="
              setAttr({
                collection: 'pages_sections',
                item: section.id,
                fields: [
                  'Color_1_override',
                  'Color_2_override',
                  'Color_3_override',
                  'Color_4_override',
                  'sticky_override',
                  'side_override',
                ],
                mode: 'modal',
              })
            "
          />
        </div>
      </div>
    </div>
    <div v-if="isVisualEditingEnabled && section" class="flex justify-center items-center gap-2">
      <Button
        id="visual-editing-button-blocks"
        class="directus-visual-editing-edit-button-blocks absolute bottom-4 left-24"
        variant="secondary"
        :data-directus="
          setAttr({
            collection: 'pages_sections',
            item: section.id,
            fields: [
              'name',
              'sticky_override',
              'Color_1_override',
              'Color_2_override',
              'Color_3_override',
              'Color_4_override',
              'blocks',
              'm2a_button',
            ],
            mode: 'drawer',
          })
        "
      >
        <Icon name="lucide:pencil" />
        Éditer la section {{ section.id }} - ↥
      </Button>
    </div>
  </section>
</template>
<style>
.directus-visual-editing-overlay.visual-editing-button-class .directus-visual-editing-edit-button-blocks {
  /* Not using style scoped because the visual editor adds it's own elements to the page. Safe to remove this if you're not using the visual editor. */
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: none;
  background: transparent;
}
</style>
